/**
 * @file offline/crm.js
 * @description Offline-capable data access for Customer Butler, on top of the
 * shared offlineStore + syncEngine. Read-through cache (refresh from server when
 * online, always serve from the local cache) and optimistic offline create
 * (write locally + queue; the engine flushes on reconnect).
 */
import { authFetch, API } from "../lib/api";
import offlineStore from "./offlineStore";
import { syncAll } from "./syncEngine";

/** Mirror the server's displayName logic so offline-created rows read right. */
function computeDisplayName(b) {
  if (b.displayName) return b.displayName;
  if (b.type === "company") return b.companyName || b.primaryEmail || "Unnamed company";
  const full = [b.firstName, b.lastName].filter(Boolean).join(" ").trim();
  return full || b.companyName || b.primaryEmail || "Unnamed customer";
}

/**
 * Load customers. Online: refresh the local cache from the server. Always:
 * return from the local cache, so the screen behaves identically offline.
 * Locally-pending (not-yet-synced) customers are included.
 * @returns {Promise<{ rows: object[], online: boolean }>}
 */
export async function loadCustomers({ search = "" } = {}) {
  const online = typeof navigator === "undefined" ? true : navigator.onLine;
  if (online) {
    try {
      const res = await authFetch(`${API}/customer/customers?search=${encodeURIComponent(search)}&limit=200`);
      if (res.ok) {
        const json = await res.json();
        await offlineStore.cacheCrmList("customers", json.data || []);
      }
    } catch (e) { /* fall through to the cache */ }
  }
  let rows = await offlineStore.getCrmAll("customers");
  rows = rows.filter((r) => !r.mergedIntoId); // hide merged-away rows
  if (search) {
    const t = search.toLowerCase();
    rows = rows.filter((r) =>
      (r.displayName || "").toLowerCase().includes(t) ||
      (r.primaryEmail || "").toLowerCase().includes(t) ||
      (r.primaryPhone || "").toLowerCase().includes(t) ||
      (r.companyName || "").toLowerCase().includes(t));
  }
  rows.sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));
  return { rows, online };
}

/** Create a customer — works offline (optimistic local write + queued sync). */
export async function createCustomerOffline(form) {
  const rec = await offlineStore.saveCrmRecord("customers", {
    type: form.type || "person",
    displayName: computeDisplayName(form),
    firstName: form.firstName || null,
    lastName: form.lastName || null,
    companyName: form.companyName || null,
    primaryEmail: form.primaryEmail || null,
    primaryPhone: form.primaryPhone || null,
    status: form.status || "active",
    source: form.source || null,
    customFields: form.customFields || {},
  });
  if (typeof navigator === "undefined" || navigator.onLine) {
    syncAll(); // fire-and-forget; no-ops if a sync is already running
  }
  return rec;
}
